import React, { useRef, useState } from 'react';

import { PLAYBOOK_STATUS_BY_RUN_STATUS, Select } from '@aline/common';
import { link } from '@aline/routes';
import { IconButton, ListItemIcon, Menu, MenuItem, Typography } from '@mui/material';
import { Copy, Pencil, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AlineTooltip, ConfirmationDialog } from 'src/components';
import { VertDotsIcon } from 'src/components/icons/VertDotsIcon';
import { useIsOrgAdmin } from 'src/gql';
import type { Apollo } from 'src/gql/generated';
import { useDispatchSuccessSnack } from 'src/helpers';
import { useDeletePlaybook, useDuplicatePlaybook } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

import { PlaybookStatusChip } from './PlaybookChip';

const MENU_ICON_SIZE = 16;

const useStyles = makeStyles()(theme => ({
  card: {
    '&:hover': {
      border: `1px solid ${theme.palette.primary.main}`,
    },
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 3,
    boxSizing: 'border-box',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    width: '100%',
  },
  description: {
    fontSize: 14,
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
  },
  menuIcon: {
    '&.MuiListItemIcon-root': {
      color: theme.appColors.iconsType,
      minWidth: 22,
    },
  },
  title: {
    fontSize: 14,
    fontWeight: 700,
  },
}));

/**
 * Everything a card renders, `runStatus` included: the list query selects the
 * playbook's rollup against the open document, so a card never queries for
 * anything of its own.
 */
export type PlaybookCardPlaybook = Pick<
  Apollo.Playbook,
  '__typename' | 'description' | 'id' | 'runStatus' | 'title'
> & { createdBy: Pick<Apollo.User, 'id'> | null };

/**
 * A version-2 playbook card: title, description, run status against the open
 * document, and an options menu. Clicking the card opens the in-panel detail
 * view; menu clicks must not trigger that navigation.
 */
export const PlaybookListCard = React.memo(function PlaybookListCard({
  onOpen,
  playbook,
}: {
  onOpen: () => void;
  playbook: PlaybookCardPlaybook;
}): JSX.Element {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { deletePlaybook } = useDeletePlaybook();
  const cardRef = useRef<HTMLDivElement | null>(null);
  const { currentUser, isOrgAdmin } = useIsOrgAdmin();
  const { duplicatePlaybook } = useDuplicatePlaybook();
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState<boolean>(false);
  const dispatchSuccessSnack = useDispatchSuccessSnack({
    key: uuid4(),
    message: 'Playbook successfully deleted.',
  });
  // The onboarding tour anchors to the example NDA's card specifically.
  const isExampleNDAPlaybook = currentUser?.settings.exampleNDAPlaybook?.id === playbook.id;
  const cardSelectors = Select.document.editor.rightDrawer.ai.panel.playbookCard;
  const canDelete = isOrgAdmin || playbook.createdBy?.id === currentUser?.id;

  return (
    <>
      <div
        ref={cardRef}
        className={classes.card}
        data-cy={isExampleNDAPlaybook ? cardSelectors.exampleNDAContainer : cardSelectors.container}
        onClick={onOpen}
      >
        <div className={classes.header}>
          <Typography noWrap className={classes.title}>
            {playbook.title}
          </Typography>
          <AlineTooltip arrow placement="bottom" title="Options">
            <div>
              <IconButton
                color="primary"
                size="small"
                onClick={event => {
                  event.preventDefault();
                  event.stopPropagation();
                  setMenuOpen(!menuOpen);
                }}
              >
                <VertDotsIcon />
              </IconButton>
            </div>
          </AlineTooltip>
        </div>
        <Typography className={classes.description}>{playbook.description}</Typography>
        <div className={classes.footer}>
          <PlaybookStatusChip status={PLAYBOOK_STATUS_BY_RUN_STATUS[playbook.runStatus]} />
        </div>
      </div>
      <Menu
        anchorEl={cardRef.current}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        open={menuOpen}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        onClose={() => setMenuOpen(false)}
      >
        <MenuItem
          dense
          onClick={() => {
            setMenuOpen(false);
            navigate(link.PlaybooksDetail({ playbookId: playbook.id }));
          }}
        >
          <ListItemIcon className={classes.menuIcon}>
            <Pencil size={MENU_ICON_SIZE} />
          </ListItemIcon>
          Edit
        </MenuItem>
        <MenuItem
          dense
          onClick={async () => {
            setMenuOpen(false);
            await duplicatePlaybook(playbook.id);
          }}
        >
          <ListItemIcon className={classes.menuIcon}>
            <Copy size={MENU_ICON_SIZE} />
          </ListItemIcon>
          Duplicate
        </MenuItem>
        <AlineTooltip
          arrow
          placement="left"
          title={!canDelete ? 'Only the playbook author or an admin can delete it' : ''}
        >
          <div>
            <MenuItem
              dense
              disabled={!canDelete}
              onClick={() => {
                setMenuOpen(false);
                setConfirmDeleteOpen(true);
              }}
            >
              <ListItemIcon className={classes.menuIcon}>
                <Trash2 size={MENU_ICON_SIZE} />
              </ListItemIcon>
              Delete
            </MenuItem>
          </div>
        </AlineTooltip>
      </Menu>
      <ConfirmationDialog
        message={`Are you sure you want to delete the ${playbook.title} playbook?  This action cannot be undone.`}
        open={confirmDeleteOpen}
        title="Delete playbook?"
        onClickPrimary={async () => {
          const { data } = await deletePlaybook(playbook.id);
          if (data) setTimeout(() => dispatchSuccessSnack(), 250);
        }}
        onClose={() => setConfirmDeleteOpen(false)}
      />
    </>
  );
});
